import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { MessageVariant } from './chat.types';

export interface ChatMessage {
  _id: string;
  chatId: string;
  senderId: string;
  receiverId: string;
  message: string;
  messageType: MessageVariant;
  status: 'ACTIVE' | 'DELETED' | 'UPLOADING';
  isRead: string[];
  isDelivered: string[];
  created: number;
  createdAt: string;
  user: {
    _id: string;
  };
  text: string;
  cartDetail?: any;
  intermediateId?: string;
  senderDetail?: any;
  mediaDetail?: any[];
  isEdited?: boolean;
}

export interface ChatInbox {
  _id: string;
  threadId: string;
  chatId: string;
  type: 'ONE_TO_ONE' | 'GROUP';
  businessChatType: string;
  contactDetails: any;
  lastMessageDetail: ChatMessage;
  noOfUnreadMessage: number;
  lastMsgCreated: number;
  senderDetail: any;
  userOnlineStatus: {
    status: boolean;
    lastSeen: string;
  };
  status?: 'ACTIVE' | 'REJECTED' | 'PENDING';
  groupName?: string;
  groupImage?: string;
  createdBy?: string;
  contactId?: string;
  members?: any[]; // For group chats
  memberDetails?: any[]; // For group member details
}

export interface BadgeCount {
  all: { [key: string]: number };
  user: { [key: string]: number };
  vendor: { [key: string]: number };
  group: { [key: string]: number };
  [key: string]: { [key: string]: number };
}

export interface ChatState {
  inbox: ChatInbox[];
  mainInbox: ChatInbox[];
  localChats: ChatInbox[];
  chatMessages: { [key: string]: ChatMessage[] };
  roomJoined: string;
  activeChatId: string;
  lastActiveChat: string;
  badgeCount: BadgeCount;
  draftedMessages: { [key: string]: { value: string } };
  mediaUploadQueue: any[];
  chatOrderQuotations: { [key: string]: any };
  chatMap: { [key: string]: any };
  fileProgress: Record<string, Record<string, number>>;
  loadingChats: boolean;
  editedMessage: any;
  blockList: any[];
}

const initialState: ChatState = {
  inbox: [],
  mainInbox: [],
  localChats: [],
  chatMessages: {},
  roomJoined: '',
  activeChatId: '',
  lastActiveChat: '',
  badgeCount: {
    all: {},
    user: {},
    vendor: {},
    group: {},
  },
  draftedMessages: {},
  mediaUploadQueue: [],
  chatOrderQuotations: {},
  chatMap: {},
  fileProgress: {},
  loadingChats: false,
  editedMessage: null,
  blockList: [],
};

// `createdAt` ISO string gives sub-second precision; fall back to `created` (ms).
const getMsgTime = (m: ChatMessage): number =>
  m.createdAt ? new Date(m.createdAt).getTime() : m.created || 0;

// Deduplicate by _id then sort chronologically.
// The _id tiebreaker makes ordering deterministic even when two messages
// share the same millisecond — avoids random reordering across platforms.
const dedupeAndSort = (messages: ChatMessage[]): ChatMessage[] => {
  const seen = new Set<string>();
  const unique = messages.filter(m => {
    if (seen.has(m._id)) return false;
    seen.add(m._id);
    return true;
  });
  unique.sort((a, b) => {
    const diff = getMsgTime(a) - getMsgTime(b);
    return diff !== 0 ? diff : a._id < b._id ? -1 : a._id > b._id ? 1 : 0;
  });
  return unique;
};

const chatSlice = createSlice({
  name: 'chat',
  initialState,
  reducers: {
    logout: state => {
      return initialState;
    },

    clearChatReducer: state => {
      return state;
    },

    // INBOX related actions
    initalInboxSetup: (
      state,
      action: PayloadAction<{ inbox: ChatInbox[] }>,
    ) => {
      state.mainInbox = action.payload.inbox;
      // Move active chat to top if present
      // console.log(' state.mainInbox', state.chatMap[state.activeChatId]);

      if (state.activeChatId) {
        const index = state.mainInbox.findIndex(
          chat => chat._id === state.activeChatId,
        );
        if (index > -1) {
          const [chat] = state.mainInbox.splice(index, 1);
          state.mainInbox.unshift({
            ...chat,
            ...state.chatMap[state.activeChatId],
          });
        }
      }
      state.inbox = action.payload.inbox;
    },

    roomJoined: (state, action: PayloadAction<{ roomId: string }>) => {
      if (!action.payload?.roomId?.length) {
        const tempChatMessages = { ...state.chatMessages };
        if (tempChatMessages[state.roomJoined]) {
          tempChatMessages[state.roomJoined] = tempChatMessages[
            state.roomJoined
          ].slice(0, 50);
        }
        state.chatMessages = tempChatMessages;
      }
      state.roomJoined = action.payload.roomId;
    },
    addToInboxList: (state, action: PayloadAction<{ inbox: ChatInbox }>) => {
      const existingIndex = state.mainInbox.findIndex(
        item => item.threadId === action.payload.inbox.threadId,
      );
      if (existingIndex === -1) {
        state.mainInbox.unshift(action.payload.inbox);
        state.inbox.unshift(action.payload.inbox);
      }
    },

    // Local chats management
    addLocalChat: (state, action: PayloadAction<{ chat: ChatInbox }>) => {
      const existingIndex = state.localChats.findIndex(
        item => item.threadId === action.payload.chat.threadId,
      );
      if (existingIndex === -1) {
        state.localChats.unshift(action.payload.chat);
      }
    },

    removeLocalChat: (state, action: PayloadAction<{ threadId: string }>) => {
      const index = state.localChats.findIndex(
        item => item.threadId === action.payload.threadId,
      );
      if (index !== -1) {
        state.localChats.splice(index, 1);
      }
    },

    clearLocalChats: state => {
      state.localChats = [];
    },

    // Chat Thread related actions
    addMessagesToChatThreadInitially: (
      state,
      action: PayloadAction<{
        threadId: string;
        message: ChatMessage[];
        pageNo: number;
      }>,
    ) => {
      const { threadId, message, pageNo } = action.payload;
      if (pageNo > 1) {
        // For pagination, add older messages to the beginning
        const existingMessages = state.chatMessages[threadId] || [];
        const allMessages = [...message, ...existingMessages];

        // Sort all messages by creation time to maintain chronological order
        allMessages.sort((a, b) => (a.created || 0) - (b.created || 0));
        state.chatMessages[threadId] = allMessages;
      } else {
        // For initial load, replace messages
        const sortedMessages = [...message];
        sortedMessages.sort((a, b) => (a.created || 0) - (b.created || 0));
        state.chatMessages[threadId] = sortedMessages;
      }
    },

    addMessagesToChatThread: (
      state,
      action: PayloadAction<{ threadId: string; message: ChatMessage[] }>,
    ) => {
      const { threadId, message } = action.payload;
      const existingMessages = state.chatMessages[threadId] || [];
      // console.log('Existing messages1:', [...existingMessages]);

      console.log('Adding messages to chat thread:', {
        threadId,
        messageCount: message.length,
        existingMessageCount: existingMessages.length,
        messages: message,
      });

      // Handle cart message updates
      message.forEach(newMsg => {
        const { created = 0 } = newMsg;
        const { cartDetail = {} } = newMsg;
        const { quotationId = '' } = cartDetail;

        existingMessages.forEach((item, index) => {
          if (
            item.messageType === 'CART' &&
            item.cartDetail?.quotationId === quotationId &&
            item.created < created
          ) {
            existingMessages[index] = {
              ...item,
              cartDetail: { ...item.cartDetail, isCartActive: false },
            };
          }
        });
      });

      // console.log('Existing messages:', existingMessages);
      // console.log('New messages:', message);
      state.chatMessages[threadId] = [...existingMessages, ...message];
    },

    updateMessageIdForMediaDoc: (
      state,
      action: PayloadAction<{
        messageId: string;
        intermediateId: string;
        threadId: string;
        msg: ChatMessage;
      }>,
    ) => {
      const { messageId, intermediateId, threadId, msg } = action.payload;
      const messages = state.chatMessages[threadId] || [];
      const messageIndex = messages.findIndex(
        m => m.intermediateId === intermediateId,
      );

      if (messageIndex !== -1) {
        const { _id, ...msgWithoutId } = msg;
        messages[messageIndex] = {
          ...messages[messageIndex],
          ...msgWithoutId,
          _id: messageId,
        };
      }
    },

    // Update optimistic message with real message ID
    updateOptimisticMessage: (
      state,
      action: PayloadAction<{
        threadId: string;
        tempId: string;
        realMessage: ChatMessage;
      }>,
    ) => {
      const { threadId, tempId, realMessage } = action.payload;
      const messages = state.chatMessages[threadId] || [];

      // Find message by intermediateId or by matching recent optimistic message
      const messageIndex = messages.findIndex(
        m =>
          m._id === tempId ||
          m.intermediateId === tempId ||
          (m.status === 'UPLOADING' && m.senderId === realMessage.senderId),
      );

      if (messageIndex !== -1) {
        console.log('Updating optimistic message at index:', messageIndex);
        messages[messageIndex] = {
          ...messages[messageIndex],
          ...realMessage,
          _id: realMessage._id,
          intermediateId: undefined, // Remove temp ID
          status: 'ACTIVE', // Ensure status is ACTIVE
        };
      } else {
        console.log('Optimistic message not found, adding as new message');
        // If optimistic message not found, add the real message
        messages.push(realMessage);
      }
    },

    increaseBadgeCountController: (
      state,
      action: PayloadAction<{ message: any }>,
    ) => {
      const { message } = action.payload;

      console.log('Badgemessage=>>>>>>', message);
      const chatId =
        (message?.businessChatType ?? message.type?.toLowerCase()).includes(
          'group',
        ) &&
        message &&
        message.hasOwnProperty('chatId')
          ? message?.chatId
          : message?._id;

      const type = (
        message?.businessChatType ?? message.type?.toLowerCase()
      ).includes('vendor')
        ? 'vendor'
        : (message?.businessChatType ?? message.type?.toLowerCase()).includes(
            'group',
          )
        ? 'group'
        : 'user';

      const updateFunc = (type: string) => {
        if (!state.badgeCount[type][chatId]) {
          return 1;
        } else {
          return state.badgeCount[type][chatId] + 1;
        }
      };

      state.badgeCount.all[chatId] = updateFunc('all');
      state.badgeCount[type][chatId] = updateFunc(type);
    },

    resetBadgeCountController: (
      state,
      action: PayloadAction<{ chatId: string }>,
    ) => {
      const { chatId } = action.payload;
      if (chatId?.length) {
        delete state.badgeCount.all[chatId];
        delete state.badgeCount.user[chatId];
        delete state.badgeCount.vendor[chatId];
        delete state.badgeCount.group[chatId];
      } else {
        state.badgeCount = { all: {}, user: {}, vendor: {}, group: {} };
      }
    },

    resetBadgeCountControllerCompletely: state => {
      state.badgeCount = { all: {}, user: {}, vendor: {}, group: {} };
    },

    resetUnreadMsgCountForInboxThread: (
      state,
      action: PayloadAction<{ threadId: string }>,
    ) => {
      const { threadId } = action.payload;
      const localIndex = state.localChats.findIndex(
        item => item._id === threadId,
      );
      if (localIndex !== -1) {
        state.localChats[localIndex].noOfUnreadMessage = 0;
      }
      const index = state.mainInbox.findIndex(item => item._id === threadId);
      if (index !== -1) {
        state.mainInbox[index].noOfUnreadMessage = 0;
      }
    },

    updateLastMessageDetail: (
      state,
      action: PayloadAction<{
        threadId: string;
        newData: ChatMessage;
        receiverInfoHolder: any;
        userOnlineStatus: any;
      }>,
    ) => {
      const { threadId, newData, receiverInfoHolder, userOnlineStatus } =
        action.payload;
      const index = state.mainInbox.findIndex(item => item._id === threadId);

      if (index >= 0) {
        state.mainInbox[index] = {
          ...state.mainInbox[index],
          lastMessageDetail: newData,
          noOfUnreadMessage: 0,
          senderDetail:
            receiverInfoHolder.senderDetail ?? newData.senderDetail ?? '',
          lastMsgCreated: newData?.created ?? +newData?.created,
        };
      } else {
        const tempData: ChatInbox = {
          contactDetails: receiverInfoHolder,
          userOnlineStatus: userOnlineStatus,
          lastMessageDetail: newData,
          noOfUnreadMessage: 0,
          lastMsgCreated: newData?.created,
          _id: newData?.chatId,
          threadId: newData?.chatId,
          chatId: newData?.chatId,
          type: 'ONE_TO_ONE',
          businessChatType: `user_${(
            receiverInfoHolder?.userType ??
            receiverInfoHolder?.contactDetails?.type ??
            'user'
          ).toLowerCase()}_chat`,
          senderDetail:
            receiverInfoHolder.senderDetail ?? newData.senderDetail ?? '',
        };

        if (
          receiverInfoHolder &&
          receiverInfoHolder.hasOwnProperty('type') &&
          receiverInfoHolder.type.toLowerCase().includes('group')
        ) {
          const groupId = receiverInfoHolder.chatId ?? newData.chatId ?? '';
          const { _id, ...receiverInfoWithoutId } = receiverInfoHolder;
          Object.assign(tempData, {
            ...receiverInfoWithoutId,
            contactDetails: {},
            senderDetail:
              receiverInfoHolder.senderDetail ?? newData.senderDetail ?? '',
            _id: groupId,
            threadId: groupId,
            chatId: groupId,
          });
        }

        state.mainInbox.push(tempData);
      }

      // Sort by last message created
      state.mainInbox.sort((a, b) => b.lastMsgCreated - a.lastMsgCreated);
      // Increment version to trigger useMemo update
      // state.mainInboxVersion += 1;
    },

    updateInboxList: (
      state,
      action: PayloadAction<{
        threadId: string;
        newData: any;
        currentUserId?: string;
      }>,
    ) => {
      const { threadId, newData, currentUserId } = action.payload;
      const indexToUpdate = state.mainInbox.findIndex(
        item => item._id === threadId,
      );

      const localIndex = state.localChats.findIndex(
        item => item._id === threadId,
      );

      const {
        contactDetails = {},
        noOfUnreadMessage = 0,
        type = '',
        _id,
        chatId = '',
        senderDetail = {},
        lastMessageDetail = {},
      }: any = newData;

      const isSyncMessage = lastMessageDetail?.senderId === currentUserId;

      console.log('newData=>>>>>>===', newData);

      // Update the chat count issue fix by uncomment
      if (indexToUpdate > -1) {
        const isGroup = type.toLowerCase().includes('group');
        const isCurrentlyActiveChat =
          state.activeChatId === threadId || isSyncMessage;

        state.mainInbox[indexToUpdate] = {
          ...state.mainInbox[indexToUpdate],
          ...(!isSyncMessage && { lastMsgCreated: newData?.lastMsgCreated }),
          lastMessageDetail,

          // Only increment unread count if user is not currently in this chat
          noOfUnreadMessage: isCurrentlyActiveChat
            ? 0
            : state.mainInbox[indexToUpdate].noOfUnreadMessage + 1,
          // noOfUnreadMessage:
          // state.mainInbox[indexToUpdate].   + 1,
          _id: isGroup ? chatId : _id,
        };

        if (isGroup) {
          state.mainInbox[indexToUpdate].lastMessageDetail = newData;
          state.mainInbox[indexToUpdate].senderDetail = senderDetail;
          state.mainInbox[indexToUpdate].lastMsgCreated = newData?.created;
        }
      } else {
        if (state.mainInbox[indexToUpdate]) {
          state.mainInbox[indexToUpdate].lastMsgCreated = newData?.created;
        }
        // state.mainInbox.unshift({ ...newData, noOfUnreadMessage: 1 });
      }

      if (localIndex > -1) {
        const isGroup = type.toLowerCase().includes('group');
        const isCurrentlyActiveChat =
          state.activeChatId === threadId || isSyncMessage;

        state.localChats[localIndex] = {
          ...state.localChats[localIndex],
          ...(!isSyncMessage && { lastMsgCreated: newData?.lastMsgCreated }),
          lastMessageDetail,

          // Only increment unread count if user is not currently in this chat
          noOfUnreadMessage: isCurrentlyActiveChat
            ? 0
            : state.localChats[localIndex].noOfUnreadMessage + 1,
          // noOfUnreadMessage:
          // state.mainInbox[indexToUpdate].   + 1,
          _id: isGroup ? chatId : _id,
        };

        if (isGroup) {
          state.localChats[localIndex].lastMessageDetail = newData;
          state.localChats[localIndex].senderDetail = senderDetail;
          state.localChats[localIndex].lastMsgCreated = newData?.created;
        }
      }

      state.mainInbox.sort((a, b) => b.lastMsgCreated - a.lastMsgCreated);
    },

    updateOnlineStatusOnInboxList: (
      state,
      action: PayloadAction<{ response: any }>,
    ) => {
      const { response } = action.payload;
      const { contactId } = response;
      let isUpdated = false;

      state.mainInbox.forEach(item => {
        if (
          item?.contactDetails?._id === contactId &&
          !item?.businessChatType.includes('group')
        ) {
          item.userOnlineStatus = response;
          isUpdated = true;
        }
      });
      state.localChats.forEach(item => {
        if (
          item?.contactDetails?._id === contactId &&
          !item?.businessChatType.includes('group')
        ) {
          item.userOnlineStatus = response;
          isUpdated = true;
        }
      });
    },

    updateDraftMessage: (
      state,
      action: PayloadAction<{ [key: string]: { value: string } }>,
    ) => {
      state.draftedMessages = { ...state.draftedMessages, ...action.payload };
    },

    uploadDocumentLocally: (
      state,
      action: PayloadAction<{ document: any; chatId: string }>,
    ) => {
      state.mediaUploadQueue.push(action.payload);
    },

    addChatOrderQuotations: (
      state,
      action: PayloadAction<{
        threadId: string;
        quotationData: any;
        quotationId: string;
      }>,
    ) => {
      const { threadId, quotationData, quotationId } = action.payload;
      if (!state.chatOrderQuotations[threadId]) {
        state.chatOrderQuotations[threadId] = {};
      }
      if (state.chatOrderQuotations[threadId][quotationId]) {
        delete state.chatOrderQuotations[threadId][quotationId];
      }
      state.chatOrderQuotations[threadId][quotationId] = quotationData;
    },

    setActiveChat: (state, action: PayloadAction<string>) => {
      state.activeChatId = action.payload;
    },

    setLastActiveChat: (state, action: PayloadAction<string>) => {
      state.lastActiveChat = action.payload;
    },

    removeGroupFromChatList: (
      state,
      action: PayloadAction<{ groupId: string }>,
    ) => {
      const { groupId } = action.payload;
      // Remove group from mainInbox
      state.mainInbox = state.mainInbox.filter(chat => chat._id !== groupId);
      // Remove group from localChats
      state.localChats = state.localChats.filter(chat => chat._id !== groupId);
      // Clear active chat if it's the group we're removing
      if (state.activeChatId === groupId) {
        state.activeChatId = '';
      }
    },

    updateChatMap: (
      state,
      action: PayloadAction<{ chatId: string; chatData: any }>,
    ) => {
      const { chatId, chatData } = action.payload;
      console.log('chatData=>>', chatData);
      state.chatMap[chatId] = chatData;
    },

    updateMessageReadStatus: (
      state,
      action: PayloadAction<{
        threadId: string;
        messageId: string;
        isRead: string[];
      }>,
    ) => {
      const { threadId, messageId, isRead } = action.payload;
      console.log('🔍 updateMessageReadStatus reducer called:', {
        threadId,
        messageId,
        isRead,
      });
      const messages = state.chatMessages[threadId] || [];
      const msg = messages.find(m => m._id === messageId);
      if (msg) {
        msg.isRead = isRead;
      } else {
        console.log('❌ Message not found in Redux state:', {
          threadId,
          messageId,
          availableMessages: messages.length,
        });
      }
    },

    updateChatReadStatus: (
      state,
      action: PayloadAction<{
        threadId: string;
        lastReadTime: number;
        readByUserId: string;
      }>,
    ) => {
      const { threadId, lastReadTime, readByUserId } = action.payload;
      console.log('🔍 updateChatReadStatus reducer called:', {
        threadId,
        lastReadTime,
        readByUserId,
      });

      const messages = state.chatMessages[threadId] || [];
      let updatedCount = 0;

      // Update all messages that were created before the read time
      messages.forEach(message => {
        const messageTime =
          message.created || new Date(message.createdAt).getTime();
        if (messageTime <= lastReadTime && message.senderId !== readByUserId) {
          const existingIsRead = Array.isArray(message.isRead)
            ? message.isRead
            : [];
          if (!existingIsRead.includes(readByUserId)) {
            message.isRead = [...existingIsRead, readByUserId];
            updatedCount++;
          }
        }
      });

      console.log(
        `✅ Updated ${updatedCount} messages with read status for chat ${threadId}`,
      );
    },

    setLoadingChats: (state, action: PayloadAction<boolean>) => {
      state.loadingChats = action.payload;
    },

    updateGroupDetails: (
      state,
      action: PayloadAction<{
        chatId: string;
        groupName: string;
        groupImage?: string;
        members?: any[];
      }>,
    ) => {
      const { chatId, groupName, groupImage, members } = action.payload;
      console.log('🔍 updateGroupDetails reducer called:', {
        chatId,
        groupName,
        groupImage,
        members,
      });

      // Update in mainInbox
      const inboxIndex = state.mainInbox.findIndex(
        item => item.chatId === chatId || item._id === chatId,
      );

      console.log('🔍 mainInbox:', state.mainInbox, inboxIndex);

      if (inboxIndex !== -1) {
        state.mainInbox[inboxIndex] = {
          ...state.mainInbox[inboxIndex],
          contactDetails: {
            ...state.mainInbox[inboxIndex].contactDetails,
          },
          groupName,
          groupImage: groupImage,
          ...(members && { members: members }),
        };
      }

      console.log('🔍 mainInbox after update:', state.mainInbox[inboxIndex]);
      console.log(
        '🔍 Updated contactDetails:',
        state.mainInbox[inboxIndex]?.contactDetails,
      );

      // Update in chatMap if exists
      if (state.chatMap[chatId]) {
        state.chatMap[chatId] = {
          ...state.chatMap[chatId],
          contactDetails: {
            ...state.chatMap[chatId].contactDetails,
          },

          groupName: groupName,
          groupImage: groupImage,
          ...(members && { members: members }),
        };
      }
    },

    groupMemberRemove: (
      state,
      action: PayloadAction<{
        chatId: string;
        leftMembers: string;
      }>,
    ) => {
      const { chatId, leftMembers } = action.payload;
      console.log('🔍 groupMemberRemove reducer called:', {
        chatId,
        leftMembers,
      });

      // Update in mainInbox
      const inboxIndex = state.mainInbox.findIndex(
        item => item.chatId === chatId || item._id === chatId,
      );

      // update in localChats

      const localIndex = state.localChats.findIndex(
        item => item.chatId === chatId || item._id === chatId,
      );

      console.log('🔍 mainInbox:', state.mainInbox, inboxIndex);

      if (inboxIndex !== -1) {
        state.mainInbox[inboxIndex] = {
          ...state.mainInbox[inboxIndex],
          contactDetails: {
            ...state.mainInbox[inboxIndex].contactDetails,
          },
          members:
            state.mainInbox[inboxIndex].members?.filter(
              (member: any) => !leftMembers.includes(member),
            ) || [],
        };
      }

      if (localIndex !== -1) {
        state.localChats[localIndex] = {
          ...state.localChats[localIndex],
          contactDetails: {
            ...state.localChats[localIndex].contactDetails,
          },
          members:
            state.localChats[localIndex].members?.filter(
              (member: any) => !leftMembers.includes(member),
            ) || [],
        };
      }

      console.log('🔍 mainInbox after update:', state.mainInbox[inboxIndex]);
      console.log(
        '🔍 Updated contactDetails ==>>>>>>>>>>>>>>:',
        state.mainInbox[inboxIndex],
      );

      // Update in chatMap if exists
      if (state.chatMap[chatId]) {
        state.chatMap[chatId] = {
          ...state.chatMap[chatId],
          contactDetails: {
            ...state.chatMap[chatId].contactDetails,
          },
          members:
            state.chatMap[chatId].members?.filter(
              (member: any) => !leftMembers.includes(member),
            ) || [],
        };
      }
    },

    groupMemberAdd: (
      state,
      action: PayloadAction<{
        chatId: string;
        members: any[];
      }>,
    ) => {
      const { chatId, members } = action.payload;
      console.log('🔍 groupMemberRemove reducer called:', {
        chatId,
        members,
      });

      // Update in mainInbox
      const inboxIndex = state.mainInbox.findIndex(
        item => item.chatId === chatId || item._id === chatId,
      );

      console.log('🔍 mainInbox:', state.mainInbox, inboxIndex);

      if (inboxIndex !== -1) {
        state.mainInbox[inboxIndex] = {
          ...state.mainInbox[inboxIndex],
          contactDetails: {
            ...state.mainInbox[inboxIndex].contactDetails,
          },
          members,
        };
      }

      console.log('🔍 mainInbox after update:', state.mainInbox[inboxIndex]);
      console.log(
        '🔍 Updated contactDetails:',
        state.mainInbox[inboxIndex]?.contactDetails,
      );

      // Update in chatMap if exists
      if (state.chatMap[chatId]) {
        state.chatMap[chatId] = {
          ...state.chatMap[chatId],
          contactDetails: {
            ...state.chatMap[chatId].contactDetails,
          },
          members,
        };
      }
    },
    updateChatStatus: (
      state,
      action: PayloadAction<{
        chatId: string;
        status: 'ACTIVE' | 'REJECTED' | 'PENDING' | 'BLOCKED';
        data?: any;
      }>,
    ) => {
      const { chatId, status, data } = action.payload;
      console.log('🔍 updateChatStatus reducer called:', {
        chatId,
        status,
        data,
      });

      // Update in mainInbox
      const inboxIndex = state.mainInbox.findIndex(
        item => item.chatId === chatId || item._id === chatId,
      );

      if (inboxIndex !== -1) {
        const updatedData = {
          ...state.mainInbox[inboxIndex],
          status,
          ...(data || {}),
        };
        if (status === 'ACTIVE') {
          delete updatedData?.blockedBy;
        }
        state.mainInbox[inboxIndex] = updatedData;
        // console.log('✅ Updated mainInbox chat status:', status);
      }

      // Update in localChats
      const localIndex = state.localChats.findIndex(
        item => item.chatId === chatId || item._id === chatId,
      );

      if (localIndex !== -1) {
        const updatedData = {
          ...state.localChats[localIndex],
          status,
          ...(data || {}),
        };
        if (status === 'ACTIVE') {
          delete updatedData?.blockedBy;
        }
        state.localChats[localIndex] = updatedData;
        // console.log('✅ Updated localChats chat status:', status);
      }

      // Update in chatMap if exists
      if (state.chatMap[chatId]) {
        state.chatMap[chatId].status = status;
        console.log('✅ Updated chatMap chat status:', status);
      }
    },
    moveChatToTop: (state, action: PayloadAction<string>) => {
      const chatId = action.payload;
      const index = state.mainInbox.findIndex(chat => chat._id === chatId);
      if (index > -1) {
        const [chat] = state.mainInbox.splice(index, 1);
        state.mainInbox.unshift(chat);

        // Increment version to trigger useMemo update
      }
    },

    updateInboxListOnSendMessage: (
      state,
      action: PayloadAction<{ threadId: string; message: ChatMessage }>,
    ) => {
      const threadId = action.payload.threadId;
      const message = action.payload.message;
      const indexToUpdate = state.mainInbox.findIndex(
        item => item._id === threadId,
      );
      const indexToUpdateInLocalChats = state.localChats.findIndex(
        item => item._id === threadId,
      );
      if (indexToUpdateInLocalChats > -1) {
        state.localChats[indexToUpdateInLocalChats].lastMsgCreated =
          new Date().getTime();
        state.localChats[indexToUpdateInLocalChats].lastMessageDetail = message;
      }
      if (indexToUpdate > -1) {
        state.mainInbox[indexToUpdate].lastMsgCreated = new Date().getTime();
        state.mainInbox[indexToUpdate].lastMessageDetail = message;
      }
    },

    // Update message status (for media upload completion)
    updateMessageStatus: (
      state,
      action: PayloadAction<{
        threadId: string;
        messageId: string;
        status: 'ACTIVE' | 'DELETED' | 'UPLOADING';
        newText?: string;
        mediaDetail?: any[];
      }>,
    ) => {
      const { threadId, messageId, status, newText, mediaDetail } =
        action.payload;
      console.log('updateMessageStatus called:', {
        threadId,
        messageId,
        status,
        newText,
      });

      const messages = state.chatMessages[threadId] || [];
      const messageIndex = messages.findIndex(
        m => m._id === messageId || m.intermediateId === messageId,
      );

      console.log(
        'Found message at index:',
        messageIndex,
        'Total messages:',
        messages.length,
      );

      if (messageIndex !== -1) {
        console.log('Updating message status:', { messageId, status, newText });
        console.log(
          'Current message mediaDetail:',
          messages[messageIndex].mediaDetail,
        );
        console.log('New mediaDetail:', mediaDetail);

        messages[messageIndex] = {
          ...messages[messageIndex],
          status,
          ...(newText && { text: newText }),
          ...(mediaDetail && { mediaDetail }),
        };

        console.log(
          'Updated message mediaDetail:',
          messages[messageIndex].mediaDetail,
        );
        console.log('Message updated successfully');
      } else {
        console.log('Message not found for update');
      }
    },

    updateInboxListData: (state, action) => {
      // console.log('action ==>', action);
      const { chatId, data } = action.payload;
      const inboxList = [...state.mainInbox];
      const localInboxList = [...state.localChats];

      // Pin message Sync  -------

      if (data?.syncMessage) {
        const findPin =
          inboxList.find(item => item?._id === data?.message.chatId)
            ?.pinnedMessages || [];

        const checkPresent = findPin.some(
          (item: any) => item._id === data?.message._id,
        );

        if (!checkPresent) {
          const updatedList = inboxList?.map(item => {
            if (item._id === chatId) {
              return { ...item, pinnedMessages: [...findPin, data?.message] };
            }
            return item;
          });

          const localUpdatedList = localInboxList?.map(item => {
            if (item._id === chatId) {
              return { ...item, pinnedMessages: [...findPin, data?.message] };
            }
            return item;
          });

          state.mainInbox = updatedList;
          state.localChats = localUpdatedList;
        }
      } else {
        const updatedList = inboxList?.map(item => {
          if (item._id === chatId) {
            return { ...item, pinnedMessages: data?.pinnedMessages };
          }
          return item;
        });

        const localUpdatedList = localInboxList?.map(item => {
          if (item._id === chatId) {
            return { ...item, pinnedMessages: data?.pinnedMessages };
          }
          return item;
        });

        console.log('localUpdatedList', localUpdatedList);

        state.mainInbox = updatedList;
        state.localChats = localUpdatedList;
      }

      // UnPin MessageSync ----

      if (data?.upPin) {
        console.log('PnPinnnnn');
        const updatedList = inboxList?.map(item => {
          if (item._id === chatId) {
            return {
              ...item,
              pinnedMessages: item?.pinnedMessages?.filter(
                (item: any) => item?._id !== data?.message?._id,
              ),
            };
          }
          return item;
        });

        const localUpdatedList = localInboxList?.map(item => {
          if (item._id === chatId) {
            return {
              ...item,
              pinnedMessages: item?.pinnedMessages?.filter(
                (item: any) => item?._id !== data?.message?._id,
              ),
            };
          }
          return item;
        });

        state.mainInbox = updatedList;
        state.localChats = localUpdatedList;
      }
    },

    updateNewMessageWithId: (state, action) => {
      const data = action.payload;
      const allChatsMap = state?.chatMessages;
      const allMessages = [...allChatsMap[data?.chatId]];

      // console.log('allMessages =>>>>>>>>>', allMessages);
      const updateMessage = allMessages?.map(item => {
        return item;
      });

      // dedupeAndSort removes the duplicate that arises when addMessagesToChatThread
      // already added the real server message before this temp→real replacement runs.
      state.chatMessages[data.chatId] = dedupeAndSort(updateMessage);
    },
    updateEditedMessage: (state, action) => {
      state.editedMessage = action.payload;
    },

    updateEditedMessageInChatThread: (state, action) => {
      const { threadId, messageId, message, isEdited } = action.payload;
      const messages = state.chatMessages[threadId] || [];
      const messageIndex = messages.findIndex(m => m._id === messageId);
      console.log('updateEditedMessageInChatThread called:', {
        messageId,
        threadId,
        messageIndex,
        length: messages.length,
      });
      if (messageIndex !== -1) {
        messages[messageIndex] = {
          ...messages[messageIndex],
          message,
          text: message,
          isEdited,
        };
      }
      state.chatMessages[threadId] = messages;

      // Update only last message only on the chat list thread
      if (true) {
        const mainInboxList = state.mainInbox;
        const updateList = mainInboxList.map((item: any) => {
          if (item._id === threadId) {
            return {
              ...item,
              pinnedMessages:
                item?.pinnedMessages?.map((item: any) => {
                  if (item._id === messageId) {
                    return {
                      ...item,
                      message,
                    };
                  }
                  return item;
                }) || [],
              ...(messageIndex === messages.length - 1 && {
                lastMessageDetail: {
                  ...item.lastMessageDetail,
                  message,
                },
              }),
            };
          }
          return item;
        });

        state.mainInbox = updateList;
      }
    },
    updateBlockList: (state, action) => {
      console.log('updateBlockList', action.payload);
      state.blockList = action.payload;
    },
    updateAdmins: (
      state,
      action: PayloadAction<{ chatId: string; admins: string[] }>,
    ) => {
      const { chatId, admins } = action.payload;

      const inboxIndex = state.mainInbox.findIndex(
        item => item.chatId === chatId || item._id === chatId,
      );
      if (inboxIndex !== -1) {
        (state.mainInbox[inboxIndex] as any).admins = admins;
      }

      const localIndex = state.localChats.findIndex(
        item => item.chatId === chatId || item._id === chatId,
      );
      if (localIndex !== -1) {
        (state.localChats[localIndex] as any).admins = admins;
      }

      if (state.chatMap[chatId]) {
        state.chatMap[chatId].admins = admins;
      }
    },
  },
});

export const {
  logout,
  clearChatReducer,
  initalInboxSetup,
  roomJoined,
  addToInboxList,
  addLocalChat,
  removeLocalChat,
  clearLocalChats,
  addMessagesToChatThreadInitially,
  addMessagesToChatThread,
  updateMessageIdForMediaDoc,
  updateOptimisticMessage,
  increaseBadgeCountController,
  resetBadgeCountController,
  resetBadgeCountControllerCompletely,
  resetUnreadMsgCountForInboxThread,
  updateLastMessageDetail,
  updateInboxList,
  updateOnlineStatusOnInboxList,
  updateDraftMessage,
  uploadDocumentLocally,
  addChatOrderQuotations,
  setActiveChat,
  setLastActiveChat,
  removeGroupFromChatList,
  updateChatMap,
  updateMessageReadStatus,
  updateChatReadStatus,
  setLoadingChats,
  updateGroupDetails,
  groupMemberRemove,
  groupMemberAdd,
  updateChatStatus,
  moveChatToTop,
  updateMessageStatus,
  updateInboxListOnSendMessage,
  updateInboxListData,
  updateNewMessageWithId,
  updateEditedMessage,
  updateEditedMessageInChatThread,
  updateBlockList,
  updateAdmins,
} = chatSlice.actions;

export default chatSlice.reducer;
